/**
 * ClientAuthContext — Area Cliente self-service (TASK 3 — FASE 3.2, feb 2026).
 *
 * Gestisce sessione cliente con:
 *   - bearer token in sessionStorage (no localStorage: XSS mitigation)
 *   - auto-refresh sliding ogni 30 min se token attivo
 *   - auto-logout dopo 15 min inattivita' (event listener)
 *   - check iniziale /api/client/me al mount
 *   - redirect a /area-cliente/login se 401
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;
const STORAGE_KEY = "colucci_client_session";
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 min

const ClientAuthContext = createContext(null);
export const useClientAuth = () => {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) throw new Error("useClientAuth must be used within ClientAuthProvider");
  return ctx;
};

export function ClientAuthProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);

  const getToken = useCallback(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }, []);

  const setToken = useCallback((token) => {
    try {
      if (token) sessionStorage.setItem(STORAGE_KEY, token);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn("sessionStorage non disponibile:", e?.message);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setAccount(null);
      setProfiles([]);
      return null;
    }
    try {
      const { data } = await axios.get(`${API}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccount(data.account);
      setProfiles(data.profiles || []);
      return data.account;
    } catch (err) {
      // 401 anonymous è il caso atteso quando l'utente non è loggato
      if (err?.response?.status === 401) {
        setToken(null);
        setAccount(null);
        setProfiles([]);
      } else {
        console.warn("Client auth check error:", err?.message);
      }
      return null;
    }
  }, [getToken, setToken]);

  const login = useCallback((token, accountData) => {
    setToken(token);
    if (accountData) setAccount(accountData);
    // resync subito per ottenere profili
    fetchMe();
  }, [setToken, fetchMe]);

  const logout = useCallback(async () => {
    const token = getToken();
    if (token) {
      try {
        await axios.post(`${API}/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        /* ignore */
      }
    }
    setToken(null);
    setAccount(null);
    setProfiles([]);
  }, [getToken, setToken]);

  // Auto-logout dopo inattivita' (FASE 4.2: niente redirect — l'utente resta
  // dove è e può semplicemente ri-aprire il LoginModal)
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      logout();
    }, IDLE_TIMEOUT_MS);
  }, [logout]);

  // Mount: check session + setup idle listeners
  useEffect(() => {
    fetchMe().finally(() => setLoading(false));
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    const handler = () => account && resetIdleTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [fetchMe, resetIdleTimer, account]);

  // Auto-refresh sliding ogni 30 min se account attivo
  useEffect(() => {
    if (!account) return;
    const id = setInterval(async () => {
      const token = getToken();
      if (!token) return;
      try {
        const { data } = await axios.post(`${API}/refresh`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data?.token) setToken(data.token);
      } catch {
        /* refresh silently */
      }
    }, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, [account, getToken, setToken]);

  return (
    <ClientAuthContext.Provider
      value={{ account, profiles, loading, login, logout, fetchMe, getToken }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
}
